import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLASS_CATEGORY_NAME_MAX_LENGTH } from '@/features/class/manage/constants';
import { useCreateClassCategoryMutation } from '@/features/class/useCreateClassCategory';
import { useClassCategories } from '@/features/class/useClassCategories';
import {
  useSaveCategoryAssignmentsMutation,
  useSortClassInCategoryMutation,
} from '@/features/class/useClassCategoryAssignments';
import {
  useRenameClassCategoryMutation,
  useSortClassCategoryMutation,
} from '@/features/class/useClassCategoryMutations';
import type { ClassCategory } from '@/features/class/boardsApi';
import { useProfileClassOrganizationStore } from '@/features/profile/classes/profileClassOrganizationStore';
import {
  useProfileClassesDraftFromLogin,
  type ProfileClassesDraftFromLogin,
} from '@/features/profile/classes/useProfileClassesFromLogin';
import { validateClassCategoryName } from '@/features/profile/classes/classCategoryValidation';
import type { ClassCategoryDraft } from '@/features/profile/classes/classCategoryTypes';
import {
  collectAssignedClassIds,
  moveItemInOrder,
} from '@/features/profile/classes/classOrganizationUtils';
import {
  applyAssignmentsToRawUser,
  buildAssignmentDiff,
} from '@/features/profile/classes/classCategoryAssignmentUtils';
import type { OwnedClassCard } from '@/features/profile/types';
import { useAuthStore } from '@/store/authStore';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import FormField from '@/shared/ui/FormField';
import Input from '@/shared/ui/Input';
import Button from '@/shared/ui/Button';
import Checkbox from '@/shared/ui/Checkbox';
import { ChevronDownIcon, PlusIcon } from '@/shared/icons';
import { iconTouchTargetClassName } from '@/shared/config/theme';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';

/** null = normal edit; 'categories' | 'uncategorized' | category id = sorting that section */
type SortMode = null | 'categories' | 'uncategorized' | string;

interface ClassCategoriesManageModalProps {
  onClose: () => void;
}

interface OrganizationDraft {
  categories: ClassCategoryDraft[];
  uncategorizedClassIds: string[];
}

/**
 * Build an editable draft.
 *
 * The category SET is authoritative from `GET /class/category` (`fetched`),
 * which includes empty categories the login payload omits. The login draft
 * supplies which classes currently sit in each category (matched by
 * stringified category id) so existing assignments are preserved. When the
 * fetch hasn't resolved yet we fall back to the login-derived categories so
 * the popup still renders the user's known groups.
 */
/**
 * Build an editable draft.
 *
 * The category set is the UNION of two sources, keyed by stringified id:
 *   • `GET /class/category` (`fetched`) — the authoritative list, including
 *     empty categories the login payload omits.
 *   • the login `boardWithCategory` (`loginDraft`) — supplies which classes
 *     currently sit in each category AND any category the GET list may not
 *     return yet (so existing assignments are never dropped).
 *
 * Login categories come first (preserving their order + membership); any
 * GET-only categories are appended. Each category's `classIds` come from the
 * login data (the only source of board↔category membership).
 */
function buildMergedDraft(
  fetched: readonly ClassCategory[] | undefined,
  loginDraft: ProfileClassesDraftFromLogin,
): OrganizationDraft {
  const loginById = new Map(
    loginDraft.categories.map((category) => [String(category.id), category]),
  );
  const fetchedById = new Map((fetched ?? []).map((category) => [String(category.id), category]));

  const orderedIds: string[] = [];
  const seen = new Set<string>();
  const pushId = (id: string) => {
    if (!seen.has(id)) {
      seen.add(id);
      orderedIds.push(id);
    }
  };

  // Login categories first (keep their backend order), then any GET-only ones.
  [...loginDraft.categories]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach((category) => pushId(String(category.id)));
  for (const category of fetched ?? []) {
    pushId(String(category.id));
  }

  const categories: ClassCategoryDraft[] = orderedIds.map((id, index) => {
    const fromLogin = loginById.get(id);
    const fromFetched = fetchedById.get(id);
    const name = (fromLogin?.name ?? fromFetched?.name ?? '').trim() || fromFetched?.name || '';
    return {
      id,
      name,
      classIds: fromLogin ? [...fromLogin.classIds] : [],
      sortOrder: index,
    };
  });

  return {
    categories,
    uncategorizedClassIds: [...loginDraft.uncategorizedClassIds],
  };
}

interface SortableRowProps {
  label: string;
  position: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  disabled?: boolean;
}

function SortableRow({
  label,
  position,
  total,
  onMoveUp,
  onMoveDown,
  disabled = false,
}: SortableRowProps) {
  const { t } = useTranslation(['profile', 'common']);

  return (
    <li className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-600">
        {position}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium text-gray-900">{label}</span>
      <div className="flex shrink-0 gap-1">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || position <= 1}
          onClick={onMoveUp}
        >
          {t('up')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || position >= total}
          onClick={onMoveDown}
        >
          {t('down')}
        </Button>
      </div>
    </li>
  );
}

export default function ClassCategoriesManageModal({ onClose }: ClassCategoriesManageModalProps) {
  const { t } = useTranslation(['profile', 'common']);
  // The manage popup mirrors the backend exactly: classes, categories and the
  // uncategorised bucket are all derived from the persisted login response
  // (`rawUser.boardWithCategory[]`, where `categoryId === -1` means
  // uncategorised) — the same source the profile panel renders from.
  const loginDraft = useProfileClassesDraftFromLogin();
  const activeClasses = loginDraft.allCards;
  const authUserId = useAuthStore((state) => state.user?.id);
  const setRawUser = useAuthStore((state) => state.setRawUser);

  // Authoritative category set (includes empties the login payload omits).
  const {
    data: fetchedCategories,
    isLoading: isLoadingCategories,
    isError: isCategoriesError,
  } = useClassCategories(authUserId);

  const applyOrganization = useProfileClassOrganizationStore((state) => state.applyOrganization);
  const openCategoryBulkAction = useProfileClassOrganizationStore(
    (state) => state.openCategoryBulkAction,
  );
  const createCategory = useCreateClassCategoryMutation();
  const saveAssignments = useSaveCategoryAssignmentsMutation();
  const sortClass = useSortClassInCategoryMutation();
  const renameCategory = useRenameClassCategoryMutation();
  const sortCategories = useSortClassCategoryMutation();

  const [draft, setDraft] = useState<OrganizationDraft>(() =>
    buildMergedDraft(fetchedCategories, loginDraft),
  );
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryError, setNewCategoryError] = useState<string | undefined>();
  const [saveError, setSaveError] = useState<string | undefined>();
  const [sortMode, setSortMode] = useState<SortMode>(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(
    () => new Set(buildMergedDraft(fetchedCategories, loginDraft).categories.map((c) => c.id)),
  );
  const [uncategorizedExpanded, setUncategorizedExpanded] = useState(true);

  // Categories that exist on the backend are "persisted" (Archive/Delete);
  // anything only in the local draft (a freshly typed, not-yet-confirmed row)
  // is removable. The GET list is the source of truth here.
  const persistedCategoryIds = useMemo(
    () => [
      ...loginDraft.categories.map((category) => category.id),
      ...(fetchedCategories ?? []).map((category) => String(category.id)),
    ],
    [fetchedCategories, loginDraft.categories],
  );

  const { categories: draftCategories, uncategorizedClassIds: draftUncategorizedIds } = draft;

  useEffect(() => {
    const next = buildMergedDraft(fetchedCategories, loginDraft);
    setDraft(next);
    setExpandedCategoryIds(new Set(next.categories.map((category) => category.id)));
  }, [fetchedCategories, loginDraft]);

  useEffect(() => {
    if (sortMode === 'categories' || sortMode === 'uncategorized') {
      setExpandedCategoryIds(new Set());
      setUncategorizedExpanded(sortMode === 'uncategorized');
      return;
    }
    if (typeof sortMode === 'string') {
      setExpandedCategoryIds(new Set([sortMode]));
      setUncategorizedExpanded(false);
    }
  }, [sortMode]);

  const cardsById = useMemo(
    () => new Map(activeClasses.map((classItem) => [classItem.id, classItem])),
    [activeClasses],
  );

  const assignedClassIds = useMemo(
    () => collectAssignedClassIds(draftCategories, []),
    [draftCategories],
  );

  const uncategorizedOrdered = useMemo(() => {
    const unassigned = new Set(
      activeClasses.map((classItem) => classItem.id).filter((id) => !assignedClassIds.has(id)),
    );
    const ordered: OwnedClassCard[] = [];
    for (const classId of draftUncategorizedIds) {
      if (unassigned.has(classId)) {
        const card = cardsById.get(classId);
        if (card) {
          ordered.push(card);
          unassigned.delete(classId);
        }
      }
    }
    for (const classItem of activeClasses) {
      if (unassigned.has(classItem.id)) {
        ordered.push(classItem);
      }
    }
    return ordered;
  }, [activeClasses, assignedClassIds, cardsById, draftUncategorizedIds]);

  const newCategoryCharactersRemaining = CLASS_CATEGORY_NAME_MAX_LENGTH - newCategoryName.length;
  const isSorting = sortMode !== null;

  const exitSortMode = useCallback(() => {
    setSortMode(null);
    setExpandedCategoryIds(new Set(draftCategories.map((category) => category.id)));
    setUncategorizedExpanded(true);
  }, [draftCategories]);

  const handleAddCategory = useCallback(async () => {
    const error = validateClassCategoryName(newCategoryName);
    if (error) {
      setNewCategoryError(error);
      return;
    }

    const trimmed = newCategoryName.trim();
    const isDuplicate = draftCategories.some(
      (category) => category.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) {
      setNewCategoryError(t('categoryNamesUnique'));
      return;
    }

    let id = `cat-${Date.now()}`;
    try {
      const created = await createCategory.mutateAsync({ name: trimmed });
      if (created) {
        id = String(created.id);
      }
    } catch (mutationError) {
      const message =
        mutationError instanceof Error && mutationError.message
          ? mutationError.message
          : t('couldNotAddCategory');
      setNewCategoryError(message);
      return;
    }

    // Optimistically show the new (empty) category immediately. The
    // `class/category` query is invalidated by the mutation, so the refetched
    // list will reconcile this row by its real backend id.
    setDraft((previous) => {
      if (previous.categories.some((category) => category.id === id)) {
        return previous;
      }
      return {
        ...previous,
        categories: [
          ...previous.categories,
          {
            id,
            name: trimmed,
            classIds: [],
            sortOrder: previous.categories.length,
          },
        ],
      };
    });
    setExpandedCategoryIds((previous) => new Set([...previous, id]));
    setNewCategoryName('');
    setNewCategoryError(undefined);
  }, [createCategory, draftCategories, newCategoryName, t]);

  const updateCategoryName = useCallback((categoryId: string, name: string) => {
    setDraft((previous) => ({
      ...previous,
      categories: previous.categories.map((category) =>
        category.id === categoryId ? { ...category, name } : category,
      ),
    }));
    setSaveError(undefined);
  }, []);

  const moveCategory = useCallback(
    async (categoryId: string, direction: 'up' | 'down') => {
      const currentIndex = draftCategories.findIndex((category) => category.id === categoryId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex < 0 || targetIndex < 0 || targetIndex >= draftCategories.length) return;

      const previousCategories = [...draftCategories];
      const nextCategories = [...draftCategories];
      [nextCategories[currentIndex], nextCategories[targetIndex]] = [
        nextCategories[targetIndex],
        nextCategories[currentIndex],
      ];
      setDraft((previous) => ({
        ...previous,
        categories: nextCategories.map((category, sortOrder) => ({ ...category, sortOrder })),
      }));

      try {
        await sortCategories.mutateAsync([{ categoryId, newPosition: targetIndex + 1 }]);
        const rawUser = useAuthStore.getState().rawUser;
        if (rawUser && Array.isArray(rawUser.boardWithCategory)) {
          const groupsByCategoryId = new Map(
            rawUser.boardWithCategory.map((group) => [
              String((group as { categoryId?: unknown }).categoryId),
              group,
            ]),
          );
          const orderedCategoryIds = new Set(nextCategories.map((category) => category.id));
          setRawUser({
            ...rawUser,
            boardWithCategory: [
              ...nextCategories
                .map((category) => groupsByCategoryId.get(category.id))
                .filter((group): group is NonNullable<typeof group> => Boolean(group)),
              ...rawUser.boardWithCategory.filter(
                (group) =>
                  !orderedCategoryIds.has(String((group as { categoryId?: unknown }).categoryId)),
              ),
            ],
          });
        }
        setSaveError(undefined);
      } catch (cause) {
        setDraft((previous) => ({ ...previous, categories: previousCategories }));
        setSaveError(cause instanceof Error ? cause.message : t('couldNotSaveChanges'));
      }
    },
    [draftCategories, setRawUser, sortCategories, t],
  );

  const toggleClassInCategory = useCallback(
    (categoryId: string, classId: string, checked: boolean) => {
      setDraft((previous) => {
        let uncategorizedClassIds = [...previous.uncategorizedClassIds];
        const categories = previous.categories.map((category) => {
          if (category.id === categoryId) {
            const classIds = checked
              ? [...category.classIds, classId]
              : category.classIds.filter((id) => id !== classId);
            return { ...category, classIds };
          }
          return {
            ...category,
            classIds: category.classIds.filter((id) => id !== classId),
          };
        });

        if (checked) {
          uncategorizedClassIds = uncategorizedClassIds.filter((id) => id !== classId);
        } else if (!collectAssignedClassIds(categories, []).has(classId)) {
          if (!uncategorizedClassIds.includes(classId)) {
            uncategorizedClassIds = [...uncategorizedClassIds, classId];
          }
        }

        return { categories, uncategorizedClassIds };
      });
      setSaveError(undefined);
    },
    [],
  );

  const moveClassInCategory = useCallback(
    async (categoryId: string, classId: string, direction: 'up' | 'down') => {
      const category = draftCategories.find((item) => item.id === categoryId);
      if (!category) return;
      const currentIndex = category.classIds.indexOf(classId);
      const newPosition = direction === 'up' ? currentIndex : currentIndex + 2;
      if (currentIndex < 0 || newPosition < 1 || newPosition > category.classIds.length) return;

      const previousOrder = [...category.classIds];
      setDraft((previous) => ({
        ...previous,
        categories: previous.categories.map((item) =>
          item.id === categoryId
            ? { ...item, classIds: moveItemInOrder(item.classIds, classId, direction) }
            : item,
        ),
      }));

      try {
        await sortClass.mutateAsync({ boardId: classId, newPosition });
        setSaveError(undefined);
      } catch (cause) {
        setDraft((previous) => ({
          ...previous,
          categories: previous.categories.map((item) =>
            item.id === categoryId ? { ...item, classIds: previousOrder } : item,
          ),
        }));
        setSaveError(cause instanceof Error ? cause.message : t('couldNotSaveChanges'));
      }
    },
    [draftCategories, sortClass, t],
  );

  const moveUncategorizedClass = useCallback(
    async (classId: string, direction: 'up' | 'down') => {
      const currentOrder = uncategorizedOrdered.map((classItem) => classItem.id);
      const currentIndex = currentOrder.indexOf(classId);
      const newPosition = direction === 'up' ? currentIndex : currentIndex + 2;
      if (currentIndex < 0 || newPosition < 1 || newPosition > currentOrder.length) return;

      const nextOrder = moveItemInOrder(currentOrder, classId, direction);
      setDraft((previous) => ({ ...previous, uncategorizedClassIds: nextOrder }));

      try {
        await sortClass.mutateAsync({ boardId: classId, newPosition });
        setSaveError(undefined);
      } catch (cause) {
        setDraft((previous) => ({ ...previous, uncategorizedClassIds: currentOrder }));
        setSaveError(cause instanceof Error ? cause.message : t('couldNotSaveChanges'));
      }
    },
    [sortClass, t, uncategorizedOrdered],
  );

  const removeCategoryFromDraft = useCallback((categoryId: string) => {
    setDraft((previous) => {
      const removed = previous.categories.find((category) => category.id === categoryId);
      const freedIds = removed?.classIds ?? [];
      const uncategorizedClassIds = [...previous.uncategorizedClassIds];
      for (const classId of freedIds) {
        if (!uncategorizedClassIds.includes(classId)) {
          uncategorizedClassIds.push(classId);
        }
      }
      return {
        categories: previous.categories
          .filter((category) => category.id !== categoryId)
          .map((category, sortOrder) => ({ ...category, sortOrder })),
        uncategorizedClassIds,
      };
    });
    setExpandedCategoryIds((previous) => {
      const next = new Set(previous);
      next.delete(categoryId);
      return next;
    });
  }, []);

  const openPersistedCategoryAction = useCallback(
    (categoryId: string, action: 'archive' | 'delete') => {
      applyOrganization({
        categories: draftCategories,
        uncategorizedClassIds: uncategorizedOrdered.map((item) => item.id),
        activeClassIds: activeClasses.map((item) => item.id),
      });
      openCategoryBulkAction(categoryId, action);
      onClose();
    },
    [
      activeClasses,
      applyOrganization,
      draftCategories,
      onClose,
      openCategoryBulkAction,
      uncategorizedOrdered,
    ],
  );

  const toggleCategoryExpanded = useCallback(
    (categoryId: string) => {
      if (isSorting) {
        return;
      }
      setExpandedCategoryIds((previous) => {
        const next = new Set(previous);
        if (next.has(categoryId)) {
          next.delete(categoryId);
        } else {
          next.add(categoryId);
        }
        return next;
      });
    },
    [isSorting],
  );

  const handleSave = useCallback(async () => {
    for (const category of draftCategories) {
      const error = validateClassCategoryName(category.name);
      if (error) {
        setSaveError(error);
        return;
      }
    }

    const names = draftCategories.map((category) => category.name.trim().toLowerCase());
    if (new Set(names).size !== names.length) {
      setSaveError(t('categoryNamesUnique'));
      return;
    }

    const uncategorizedOrder = uncategorizedOrdered.map((classItem) => classItem.id);
    const nextCategories = draftCategories.map((category, index) => ({
      id: category.id,
      name: category.name.trim(),
      classIds: category.classIds.filter((id) => cardsById.has(id)),
      sortOrder: index,
    }));

    // Persist every board whose category/position changed vs the login layout.
    const diff = buildAssignmentDiff(
      {
        categories: loginDraft.categories,
        uncategorizedClassIds: loginDraft.uncategorizedClassIds,
      },
      { categories: nextCategories, uncategorizedClassIds: uncategorizedOrder },
    );

    try {
      const originalNames = new Map(
        [
          ...loginDraft.categories,
          ...(fetchedCategories ?? []).map((item) => ({
            id: String(item.id),
            name: item.name,
            classIds: [],
            sortOrder: 0,
          })),
        ].map((category) => [String(category.id), category.name.trim()]),
      );
      for (const category of nextCategories) {
        if (originalNames.has(category.id) && originalNames.get(category.id) !== category.name) {
          await renameCategory.mutateAsync({ categoryId: category.id, name: category.name });
        }
      }
      if (diff.length > 0) {
        await saveAssignments.mutateAsync(diff);
      }
    } catch (mutationError) {
      const message =
        mutationError instanceof Error && mutationError.message
          ? mutationError.message
          : t('couldNotSaveChanges');
      setSaveError(message);
      return;
    }

    // Keep the local org store in sync (some consumers still read it).
    applyOrganization({
      categories: nextCategories,
      uncategorizedClassIds: uncategorizedOrder,
      // Classes come from the login response (real backend board ids), so the
      // store must treat these ids as active instead of the seed manage store.
      activeClassIds: activeClasses.map((classItem) => classItem.id),
    });

    // Rewrite the login payload so the profile "Your classes" section reflects
    // the new grouping immediately (reassigned classes leave uncategorised).
    setRawUser(
      applyAssignmentsToRawUser(
        useAuthStore.getState().rawUser,
        nextCategories,
        uncategorizedOrder,
        activeClasses,
      ),
    );

    setSaveError(undefined);
    onClose();
  }, [
    activeClasses,
    applyOrganization,
    cardsById,
    draftCategories,
    loginDraft,
    fetchedCategories,
    onClose,
    saveAssignments,
    renameCategory,
    setRawUser,
    uncategorizedOrdered,
    t,
  ]);

  const sortModeLabel =
    sortMode === 'categories'
      ? t('sortingCategoryOrder')
      : sortMode === 'uncategorized'
        ? t('sortingUncategorized')
        : typeof sortMode === 'string'
          ? t('sortingClassesIn', {
              name:
                draftCategories.find((category) => category.id === sortMode)?.name ??
                t('categoryName'),
            })
          : null;

  return (
    <Modal
      onClose={onClose}
      zIndexClassName="z-[100]"
      className="flex max-h-[min(92vh,720px)] w-full max-w-2xl flex-col"
      ariaLabel={t('manageCategoriesAria')}
    >
      <ModalHeader title={t('manageCategories')} onClose={onClose} />

      {isSorting && sortModeLabel ? (
        <div className="flex items-center justify-between gap-3 border-b border-brand-100 bg-brand-50/60 px-4 py-2.5 sm:px-6">
          <p className={cn(textStyles.label, 'text-brand-emphasis')}>{sortModeLabel}</p>
          <Button type="button" variant="primary" size="sm" onClick={exitSortMode}>
            {t('doneSorting')}
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-5">
          {sortMode === 'categories' ? (
            <section>
              <p className={cn(textStyles.caption, 'mb-3')}>{t('sortCategoryOrderHint')}</p>
              <ul className="space-y-2">
                {draftCategories.map((category, index) => (
                  <SortableRow
                    key={category.id}
                    label={category.name.trim() || t('untitledCategory')}
                    position={index + 1}
                    total={draftCategories.length}
                    disabled={sortCategories.isPending}
                    onMoveUp={() => moveCategory(category.id, 'up')}
                    onMoveDown={() => moveCategory(category.id, 'down')}
                  />
                ))}
              </ul>
            </section>
          ) : null}

          {sortMode === 'uncategorized' ? (
            <section>
              <p className={cn(textStyles.caption, 'mb-3')}>{t('uncategorizedTopHint')}</p>
              {uncategorizedOrdered.length === 0 ? (
                <p className={textStyles.bodyMuted}>{t('noUncategorizedClasses')}</p>
              ) : (
                <ul className="space-y-2">
                  {uncategorizedOrdered.map((classItem, index) => (
                    <SortableRow
                      key={classItem.id}
                      label={classItem.name}
                      position={index + 1}
                      total={uncategorizedOrdered.length}
                      disabled={sortClass.isPending}
                      onMoveUp={() => moveUncategorizedClass(classItem.id, 'up')}
                      onMoveDown={() => moveUncategorizedClass(classItem.id, 'down')}
                    />
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {typeof sortMode === 'string' && sortMode !== 'categories' && sortMode !== 'uncategorized'
            ? (() => {
                const category = draftCategories.find((item) => item.id === sortMode);
                if (!category) {
                  return null;
                }
                const orderedClasses = category.classIds
                  .map((classId) => cardsById.get(classId))
                  .filter((item): item is OwnedClassCard => Boolean(item));
                return (
                  <section>
                    <p className={cn(textStyles.caption, 'mb-3')}>
                      {t('sortClassesInCategoryHint')}
                    </p>
                    {orderedClasses.length === 0 ? (
                      <p className={textStyles.bodyMuted}>{t('noClassesInCategory')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {orderedClasses.map((classItem, index) => (
                          <SortableRow
                            key={classItem.id}
                            label={classItem.name}
                            position={index + 1}
                            total={orderedClasses.length}
                            disabled={sortClass.isPending}
                            onMoveUp={() => moveClassInCategory(category.id, classItem.id, 'up')}
                            onMoveDown={() =>
                              moveClassInCategory(category.id, classItem.id, 'down')
                            }
                          />
                        ))}
                      </ul>
                    )}
                  </section>
                );
              })()
            : null}

          {!isSorting ? (
            <>
              {isLoadingCategories && !fetchedCategories ? (
                <p className={textStyles.bodyMuted}>{t('loadingCategories')}</p>
              ) : null}
              {isCategoriesError ? (
                <p className="text-sm text-amber-600">{t('categoriesLoadError')}</p>
              ) : null}
              <section className="rounded-xl border border-gray-300 bg-gray-50/50">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
                  onClick={() => setUncategorizedExpanded((value) => !value)}
                  aria-expanded={uncategorizedExpanded}
                >
                  <div>
                    <p className={textStyles.label}>{t('uncategorizedClasses')}</p>
                    <p className={cn(textStyles.caption, 'mt-0.5')}>
                      {t('atTopOfProfile', { count: uncategorizedOrdered.length })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        setSortMode('uncategorized');
                      }}
                    >
                      {t('sortOrder')}
                    </Button>
                    <ChevronDownIcon
                      className={cn(
                        'h-5 w-5 shrink-0 text-gray-500 transition-transform',
                        uncategorizedExpanded && 'rotate-180',
                      )}
                    />
                  </div>
                </button>
                {uncategorizedExpanded ? (
                  <div className="border-t border-gray-300 px-4 pb-3 pt-2">
                    {uncategorizedOrdered.length === 0 ? (
                      <p className={textStyles.bodyMuted}>{t('allClassesInCategory')}</p>
                    ) : (
                      <ul className="space-y-1">
                        {uncategorizedOrdered.map((classItem, index) => (
                          <li key={classItem.id} className="text-sm text-gray-800">
                            {index + 1}. {classItem.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </section>

              {draftCategories.length > 0 ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSortMode('categories')}
                  >
                    {t('sortCategoryOrder')}
                  </Button>
                </div>
              ) : null}

              {draftCategories.map((category) => {
                const isExpanded = expandedCategoryIds.has(category.id);
                const orderedInCategory = category.classIds
                  .map((classId) => cardsById.get(classId))
                  .filter((item): item is OwnedClassCard => Boolean(item));

                return (
                  <section
                    key={category.id}
                    className="rounded-xl border border-gray-300 bg-white shadow-sm"
                  >
                    <div className="flex items-start gap-2 px-4 py-3">
                      <button
                        type="button"
                        className={cn(iconTouchTargetClassName, 'mt-1 shrink-0')}
                        onClick={() => toggleCategoryExpanded(category.id)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? t('collapseCategory') : t('expandCategory')}
                      >
                        <ChevronDownIcon
                          className={cn(
                            'h-5 w-5 text-gray-500 transition-transform',
                            isExpanded && 'rotate-180',
                          )}
                        />
                      </button>
                      <div className="min-w-0 flex-1">
                        <FormField
                          label={t('categoryName')}
                          htmlFor={`category-name-${category.id}`}
                          required
                        >
                          <Input
                            id={`category-name-${category.id}`}
                            value={category.name}
                            onChange={(event) =>
                              updateCategoryName(category.id, event.target.value)
                            }
                            maxLength={CLASS_CATEGORY_NAME_MAX_LENGTH}
                          />
                        </FormField>
                        <p className={cn(textStyles.caption, 'mt-1 tabular-nums')}>
                          {t('class', { count: orderedInCategory.length })}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1 sm:flex-row sm:flex-wrap">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSortMode(category.id)}
                          disabled={orderedInCategory.length < 2}
                        >
                          {t('sortClasses')}
                        </Button>
                        {persistedCategoryIds.includes(category.id) ? (
                          <>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => openPersistedCategoryAction(category.id, 'archive')}
                            >
                              {t('archive')}
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              size="sm"
                              onClick={() => openPersistedCategoryAction(category.id, 'delete')}
                            >
                              {t('delete')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCategoryFromDraft(category.id)}
                          >
                            {t('remove')}
                          </Button>
                        )}
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                        <p className={cn(textStyles.caption, 'mb-3')}>{t('tickClassesHint')}</p>
                        <ul className="space-y-2">
                          {activeClasses.map((classItem) => {
                            const isInCategory = category.classIds.includes(classItem.id);
                            const inOtherCategory = draftCategories.some(
                              (other) =>
                                other.id !== category.id && other.classIds.includes(classItem.id),
                            );
                            const checkboxId = `${category.id}-${classItem.id}`;
                            return (
                              <li
                                key={classItem.id}
                                className={cn(
                                  'rounded-lg border px-3 py-2',
                                  isInCategory
                                    ? 'border-brand-100 bg-brand-50/40'
                                    : 'border-gray-300',
                                )}
                              >
                                <Checkbox
                                  id={checkboxId}
                                  checked={isInCategory}
                                  disabled={inOtherCategory && !isInCategory}
                                  onChange={(event) =>
                                    toggleClassInCategory(
                                      category.id,
                                      classItem.id,
                                      event.target.checked,
                                    )
                                  }
                                  label={
                                    <span className="flex min-w-0 flex-col">
                                      <span className="text-sm font-medium text-gray-900">
                                        {classItem.name}
                                      </span>
                                      {inOtherCategory && !isInCategory ? (
                                        <span className={textStyles.caption}>
                                          {t('alreadyInAnotherCategory')}
                                        </span>
                                      ) : null}
                                    </span>
                                  }
                                />
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}
                  </section>
                );
              })}

              <section className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 p-4">
                <p className={textStyles.label}>{t('newCategory')}</p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <FormField
                    label={t('categoryName')}
                    htmlFor="new-category-name"
                    required
                    error={newCategoryError}
                    hint={t('bioRemaining', { count: newCategoryCharactersRemaining })}
                    className="min-w-0 flex-1"
                  >
                    <Input
                      id="new-category-name"
                      value={newCategoryName}
                      onChange={(event) => {
                        setNewCategoryName(event.target.value);
                        setNewCategoryError(undefined);
                      }}
                      placeholder={t('newCategoryPlaceholder')}
                      maxLength={CLASS_CATEGORY_NAME_MAX_LENGTH}
                      hasError={Boolean(newCategoryError)}
                    />
                  </FormField>
                  <Button
                    type="button"
                    variant="secondary"
                    leftIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={() => {
                      void handleAddCategory();
                    }}
                    disabled={createCategory.isPending || newCategoryName.trim().length === 0}
                  >
                    {createCategory.isPending ? t('addingCategory') : t('addCategory')}
                  </Button>
                </div>
              </section>
            </>
          ) : null}

          {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}
        </div>
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-300 px-4 py-3 sm:px-6">
        {isSorting ? (
          <Button type="button" variant="primary" onClick={exitSortMode}>
            {t('doneSorting')}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={saveAssignments.isPending}
            >
              {t('common:cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saveAssignments.isPending}
            >
              {saveAssignments.isPending ? t('common:saving') : t('saveChanges')}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
